import React, { useContext, useMemo } from 'react';
import { getLocale, useIntl } from 'umi';
import { formatNum, formatPercentNum, unitConversion } from '@/pages/dashboard/utils/utils';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import Revenue from './Revenue';
import { usePCRevenueColumns } from '../pcConsoleRevenue/components/PcRevenue';
import { isArray, isEmpty } from 'lodash-es';
import { axisLabelFormat, formatterFunc } from '../pcConsoleRevenue/components/PcPayerAndPayer';

function PayerAndPayer(props: any) {
	const contextObj = useContext(FilterContext);
	const { data: defaultData = [], enName, cnName, defaultActive = 1, config: configs } = props;
	const { filterVal: formValue } = contextObj || {};
	const { data: dataSource, loading, errorMsg } = defaultData?.MonetizationRevenueData || {};
	const data = dataSource?.payer;
	const { componentMetricsConfig } = configs;
	const { locale } = useIntl();
	const isEn = locale === 'en-US';
	const { timeType = 'Day' } = formValue;
	const payerAndPayerData = useMemo(() => {
		if (!isArray(data) || isEmpty(data)) return [];
		const dataSource = data?.map(item => {
			return {
				date: item.Date,
				system: '',
				metrics: {
					mtx_payer_rate: {
						value: item.PayerRatio,
						dod: '',
						sdlw: '',
						change: ''
					},
					mtx_payers: {
						value: item.Payer,
						dod: '',
						sdlw: '',
						change: ''
					}
				}
			};
		});
		return dataSource;
	}, [data]);

	const { tableOption, chart, tableData, downloadData } = usePCRevenueColumns(
		payerAndPayerData,
		componentMetricsConfig,
		timeType
	);

	// 双轴自定义配置
	const config = useMemo(() => {
		const yAxis = [
			{
				type: 'value',
				position: 'left',
				splitLine: {
					show: true,
					lineStyle: {
						type: 'dashed',
						color: '#ddd'
					}
				},
				axisLabel: {
					color: '#888888',
					fontSize: 12,
					formatter: (value: string | number) => unitConversion(value)
				}
			},
			{
				type: 'value',
				position: 'right',
				alignTicks: true, // 多Y轴时，开启是否对齐刻度线
				splitLine: {
					show: true,
					lineStyle: {
						type: 'dashed',
						color: '#ddd'
					}
				},
				axisLabel: {
					formatter: (value: number) => {
						const num = value < 0.01 ? 2 : 0;
						return (value && `${formatPercentNum(value, num)}`) || value;
					}
				}
			}
		];
		return {
			bar: {
				formatterFunc: (params: any) => formatterFunc(params, timeType, isEn, componentMetricsConfig),
				setOtherConfigFunc: (opt: {
					xAxis: any;
					series: any[];
					yAxis: (
						| {
								type: string;
								position: string;
								splitLine: { show: boolean; lineStyle: { type: string; color: string } };
								axisLabel: { color: string; fontSize: number; formatter: (value: any) => string };
								alignTicks?: undefined;
						  }
						| {
								type: string;
								position: string;
								alignTicks: boolean; // 多Y轴时，开启是否对齐刻度线
								splitLine: { show: boolean; lineStyle: { type: string; color: string } };
								axisLabel: { formatter: (value: any) => any; color?: undefined; fontSize?: undefined };
						  }
					)[];
				}) => {
					opt.xAxis = axisLabelFormat(opt, timeType);
					opt.series = opt.series?.map((item: any, index: number) => {
						return {
							...item,
							type: index === 0 ? 'bar' : 'line',
							yAxisIndex: index
						};
					});
					opt.yAxis = yAxis;
					return opt;
				}
			},
			line: {
				formatterFunc: (params: any) => formatterFunc(params, timeType, isEn, componentMetricsConfig),
				setOtherConfigFunc: (opt: {
					xAxis: any;
					series: any[];
					yAxis: (
						| {
								type: string;
								position: string;
								splitLine: { show: boolean; lineStyle: { type: string; color: string } };
								axisLabel: { color: string; fontSize: number; formatter: (value: any) => string };
								alignTicks?: undefined;
						  }
						| {
								type: string;
								position: string;
								alignTicks: boolean; // 多Y轴时，开启是否对齐刻度线
								splitLine: { show: boolean; lineStyle: { type: string; color: string } };
								axisLabel: { formatter: (value: any) => any; color?: undefined; fontSize?: undefined };
						  }
					)[];
				}) => {
					opt.xAxis = axisLabelFormat(opt, timeType);
					opt.series = opt.series?.map((item: any, index: any) => {
						return {
							...item,
							yAxisIndex: index
						};
					});
					opt.yAxis = yAxis;
					return opt;
				}
			}
		};
	}, [payerAndPayerData, getLocale()]);

	// 折线图 && 表格
	const options = useMemo(() => {
		return {
			chart,
			table: {
				option: tableOption,
				data: tableData,
				downloadData
			}
		};
	}, [payerAndPayerData, getLocale()]);

	return (
		<Revenue
			{...{
				otherOption: options,
				data: {
					MonetizationRevenueData: {
						data: {
							revenue: payerAndPayerData
						},
						loading,
						errorMsg
					}
				},
				enName,
				cnName,
				renderFormatterFunc: (params: any) => {
					const result = formatNum(params?.value, false, true);
					return result === '0.00' ? 0 : result;
				},
				otherConfig: config,
				defaultActive,
				isShowLegend: true
			}}
		/>
	);
}

export default PayerAndPayer;
